import { addToolBreadcrumb } from '@tunasong/models'
import {
  computeVoiceLeadingSessionStats,
  generateVoiceLeadingQuizQuestion,
  getVoicingPair,
  validateVoiceLeadingQuizAnswer,
} from '@tunasong/music-lib'
import type {
  ChordPair,
  VoicedChord,
  VoiceLeadingIdiom,
  VoiceLeadingKey,
  VoiceLeadingQuizQuestion,
  VoiceLeadingQuizResult,
  VoiceLeadingQuizVerdict,
} from '@tunasong/music-lib'
import { useSearchParams } from '@tunasong/ui-lib'
import { useEffect, useState, type FC } from 'react'
import { buildVoiceLeadingStabs } from '../player/voice-leading-sequencer.js'
import { RelatedTools, type RelatedTool } from '../related-tools/related-tools.js'
import { useVoiceLeadingAudition } from './voice-leading-lab-playback.js'
import {
  parseChordPair,
  parseVoiceLeadingIdiom,
  parseVoiceLeadingKey,
  parseVoiceLeadingSubMode,
} from './voice-leading-lab-params.js'
import { VoiceLeadingLabSummary } from './voice-leading-lab-summary.js'
import { VoiceLeadingLabWelcome } from './voice-leading-lab-welcome.js'
import {
  VoiceLeadingExplorePanel,
  type VoiceLeadingExploreSelection,
  type VoiceLeadingVoicingSlot,
} from './voice-leading-explore-panel.js'
import { VoiceLeadingQuizFeedback } from './voice-leading-quiz-feedback.js'
import { VoiceLeadingQuizQuestionPanel } from './voice-leading-quiz-question.js'
import styles from './voice-leading-lab.module.css'

const QUESTIONS_PER_SESSION = 10
const TEMPO_BPM = 100

type SessionState =
  | { phase: 'welcome'; preSubMode: 'explore' | 'quiz' | null }
  | { phase: 'exploring'; pair: ChordPair; musicKey: VoiceLeadingKey; idiom: VoiceLeadingIdiom }
  | {
      phase: 'asking-quiz'
      index: number
      idiom: VoiceLeadingIdiom
      question: VoiceLeadingQuizQuestion
      results: VoiceLeadingQuizResult[]
    }
  | {
      phase: 'feedback-quiz'
      index: number
      idiom: VoiceLeadingIdiom
      question: VoiceLeadingQuizQuestion
      verdict: VoiceLeadingQuizVerdict
      results: VoiceLeadingQuizResult[]
    }
  | { phase: 'done-quiz'; results: VoiceLeadingQuizResult[] }

const EMPTY_VOICING: VoicedChord = { pitches: [] }

/** Voicings as currently presented to the user — slot A first, slot B second. */
const presentedVoicings = (
  question: VoiceLeadingQuizQuestion
): { a: VoicedChord; b: VoicedChord } => {
  const { voicingPair, presentationOrder } = question
  const pick = (which: 'smooth' | 'awkward'): VoicedChord => {
    const list = which === 'smooth' ? voicingPair.smooth : voicingPair.awkward
    return { pitches: [...list[0].pitches, ...list[1].pitches] }
  }
  return { a: pick(presentationOrder[0]), b: pick(presentationOrder[1]) }
}

const revealDelayMs = (a: VoicedChord, b: VoicedChord): number =>
  buildVoiceLeadingStabs({ voicingA: a, voicingB: b, bpm: TEMPO_BPM }).phaseBoundaries.endMs

/**
 * Voice-Leading Lab v1 host. Two sub-modes (Explore, Quiz); 10-question Quiz
 * sessions; locked tempo 100 BPM. Mirrors the Form Visualizer clone-per-sub-mode
 * state machine. Idiom is threaded from the welcome picker into both sub-modes.
 *
 * The Quiz audition plays the two voicings in their presented order (slot A then
 * slot B), so per-voicing replay maps slot A → voicing-only-a, slot B → -b.
 *
 * Spec: docs/superpowers/specs/2026-05-27-voice-leading-lab-v1-design.md §6
 */
export const VoiceLeadingLab: FC = () => {
  const [searchParams] = useSearchParams()
  const [state, setState] = useState<SessionState>(() => {
    const preSubMode = parseVoiceLeadingSubMode(searchParams.get('mode'))
    const idiom = parseVoiceLeadingIdiom(searchParams.get('idiom'))
    if (preSubMode === 'quiz') {
      return {
        phase: 'asking-quiz',
        index: 0,
        idiom,
        question: generateVoiceLeadingQuizQuestion(idiom),
        results: [],
      }
    }
    if (preSubMode === 'explore') {
      return {
        phase: 'exploring',
        pair: parseChordPair(searchParams.get('pair')) ?? 'I-V',
        musicKey: parseVoiceLeadingKey(searchParams.get('key')) ?? 'C',
        idiom,
      }
    }
    return { phase: 'welcome', preSubMode }
  })

  useEffect(() => {
    addToolBreadcrumb('opened', { tool: 'voice-leading' })
  }, [])

  // Resolve the voicing pair feeding the audition hook for the current phase.
  let auditionA: VoicedChord = EMPTY_VOICING
  let auditionB: VoicedChord = EMPTY_VOICING
  if (state.phase === 'exploring') {
    const voicingPair = getVoicingPair(state.musicKey, state.pair)
    // Default audition target = smooth voicing (explore play buttons re-target).
    auditionA = { pitches: [...voicingPair.smooth[0].pitches, ...voicingPair.smooth[1].pitches] }
    auditionB = { pitches: [...voicingPair.awkward[0].pitches, ...voicingPair.awkward[1].pitches] }
  } else if (state.phase === 'asking-quiz' || state.phase === 'feedback-quiz') {
    const presented = presentedVoicings(state.question)
    auditionA = presented.a
    auditionB = presented.b
  }
  const audition = useVoiceLeadingAudition({ voicingA: auditionA, voicingB: auditionB, bpm: TEMPO_BPM })

  const handleStartExplore = (idiom: VoiceLeadingIdiom): void => {
    addToolBreadcrumb('opened', { tool: 'voice-leading', mode: 'explore', idiom })
    setState({ phase: 'exploring', pair: 'I-V', musicKey: 'C', idiom })
  }

  const handleStartQuiz = (idiom: VoiceLeadingIdiom): void => {
    addToolBreadcrumb('opened', { tool: 'voice-leading', mode: 'quiz', idiom })
    setState({
      phase: 'asking-quiz',
      index: 0,
      idiom,
      question: generateVoiceLeadingQuizQuestion(idiom),
      results: [],
    })
  }

  const handleExploreChange = (selection: VoiceLeadingExploreSelection): void => {
    addToolBreadcrumb('opened', {
      tool: 'voice-leading',
      mode: 'explore',
      pair: selection.pair,
      key: selection.musicKey,
      idiom: selection.idiom,
    })
    setState({
      phase: 'exploring',
      pair: selection.pair,
      musicKey: selection.musicKey,
      idiom: selection.idiom,
    })
  }

  const handleExplorePlay = (slot: VoiceLeadingVoicingSlot): void => {
    audition.play(slot === 'smooth' ? 'voicing-only-a' : 'voicing-only-b')
  }

  const handleAnswer = (picked: 'A' | 'B'): void => {
    if (state.phase !== 'asking-quiz') {
      return
    }
    const verdict = validateVoiceLeadingQuizAnswer(state.question, picked)
    addToolBreadcrumb('answered', {
      tool: 'voice-leading',
      subMode: 'quiz',
      correct: verdict.correct,
      pair: state.question.voicingPair.pair,
    })
    setState({
      phase: 'feedback-quiz',
      index: state.index,
      idiom: state.idiom,
      question: state.question,
      verdict,
      results: state.results,
    })
  }

  const handleReplayVoicing = (slot: 'A' | 'B'): void => {
    audition.play(slot === 'A' ? 'voicing-only-a' : 'voicing-only-b')
  }

  const handleNext = (): void => {
    if (state.phase !== 'feedback-quiz') {
      return
    }
    const results: VoiceLeadingQuizResult[] = [
      ...state.results,
      { pair: state.question.voicingPair.pair, correct: state.verdict.correct },
    ]
    if (state.index + 1 >= QUESTIONS_PER_SESSION) {
      setState({ phase: 'done-quiz', results })
      return
    }
    setState({
      phase: 'asking-quiz',
      index: state.index + 1,
      idiom: state.idiom,
      question: generateVoiceLeadingQuizQuestion(state.idiom),
      results,
    })
  }

  const handleRestart = (): void => {
    setState({
      phase: 'asking-quiz',
      index: 0,
      idiom: 'classical',
      question: generateVoiceLeadingQuizQuestion('classical'),
      results: [],
    })
  }

  const handleSwitchToExplore = (): void => {
    setState({ phase: 'welcome', preSubMode: 'explore' })
  }

  const footerTools: RelatedTool[] = [
    { targetRoute: '/app/reharm', lexiconId: 'crosslink-voice-leading-to-reharm' },
    { targetRoute: '/music-theory', lexiconId: 'crosslink-voice-leading-to-suite' },
  ]

  if (state.phase === 'welcome') {
    return (
      <div className={styles.container}>
        <VoiceLeadingLabWelcome
          preSelectedSubMode={state.preSubMode}
          preSelectedIdiom="classical"
          onStartExplore={handleStartExplore}
          onStartQuiz={handleStartQuiz}
        />
        <RelatedTools tools={footerTools} />
      </div>
    )
  }

  if (state.phase === 'exploring') {
    return (
      <div className={styles.container}>
        <VoiceLeadingExplorePanel
          pair={state.pair}
          musicKey={state.musicKey}
          idiom={state.idiom}
          onChange={handleExploreChange}
          onPlayVoicing={handleExplorePlay}
        />
        <RelatedTools tools={footerTools} />
      </div>
    )
  }

  if (state.phase === 'asking-quiz') {
    const scoreCorrect = state.results.filter(r => r.correct).length
    return (
      <div className={styles.container}>
        <VoiceLeadingQuizQuestionPanel
          scoreCorrect={scoreCorrect}
          scoreAnswered={state.results.length}
          revealDelayMs={revealDelayMs(auditionA, auditionB)}
          onHearIt={() => audition.play('pair')}
          onAnswer={handleAnswer}
        />
      </div>
    )
  }

  if (state.phase === 'feedback-quiz') {
    return (
      <div className={styles.container}>
        <VoiceLeadingQuizFeedback
          question={state.question}
          verdict={state.verdict}
          onReplayVoicing={handleReplayVoicing}
          onNext={handleNext}
        />
      </div>
    )
  }

  // state.phase === 'done-quiz'
  return (
    <div className={styles.container}>
      <VoiceLeadingLabSummary
        stats={computeVoiceLeadingSessionStats(state.results)}
        onRestart={handleRestart}
        onSwitchToExplore={handleSwitchToExplore}
      />
    </div>
  )
}
